import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useApiQuery } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import { ApiResponse } from '@/shared/types/apiTypes';

import {
  ContactFilter,
  ContactListParams,
  ContactListResponse,
} from '../types/contactTypes';

const ITEMS_PER_PAGE = 30;

const fetchContactList = async (
  params: ContactListParams,
): Promise<ApiResponse<ContactListResponse>> => {
  const searchParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
  });

  if (params.keyword && params.keyword.trim()) {
    searchParams.append('keyword', params.keyword.trim());
  }

  if (params.filter && params.filter !== 'ALL') {
    searchParams.append('filter', params.filter);
  }

  return api.get<ContactListResponse>(
    `/api/v1/contact/list?${searchParams.toString()}`,
  );
};

export const useContactList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [filter, setFilter] = useState<ContactFilter>('ALL');
  const [shouldReset, setShouldReset] = useState(false);

  // URL에서 파라미터 초기화
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get('page');
    const keywordParam = searchParams.get('keyword');
    const filterParam = searchParams.get('filter');

    const page = pageParam ? parseInt(pageParam, 10) : 1;
    const searchKeyword = keywordParam || '';
    const currentFilter = (filterParam as ContactFilter) || 'ALL';

    setCurrentPage(page);
    setKeyword(searchKeyword);
    setFilter(currentFilter);

    // URL에 기본 파라미터가 없으면 추가
    if (!pageParam) {
      searchParams.set('page', '1');
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location, navigate]);

  // Contact 목록 조회
  const {
    data: contactListData,
    isLoading,
    error,
    refetch,
  } = useApiQuery(
    ['contactList', currentPage, keyword, filter],
    () =>
      fetchContactList({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        keyword,
        filter,
      }),
    {
      enabled: true,
    },
  );

  // URL 업데이트 함수
  const updateURL = useCallback(
    (page: number, searchKeyword: string, currentFilter: ContactFilter) => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', page.toString());

      if (searchKeyword && searchKeyword.trim()) {
        searchParams.set('keyword', searchKeyword.trim());
      }

      if (currentFilter !== 'ALL') {
        searchParams.set('filter', currentFilter);
      }

      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    },
    [location.pathname, navigate],
  );

  // 페이지 변경
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      updateURL(page, keyword, filter);
    },
    [keyword, filter, updateURL],
  );

  // 검색 처리
  const handleSearch = useCallback(
    (searchTerm: string) => {
      setKeyword(searchTerm);
      setCurrentPage(1);
      updateURL(1, searchTerm, filter);
    },
    [filter, updateURL],
  );

  // 필터 변경 처리
  const handleFilterChange = useCallback(
    (newFilter: ContactFilter) => {
      setFilter(newFilter);
      setCurrentPage(1);
      updateURL(1, keyword, newFilter);
    },
    [keyword, updateURL],
  );

  // 초기화 처리
  const handleReset = useCallback(() => {
    setKeyword('');
    setFilter('ALL');
    setCurrentPage(1);
    setShouldReset(true);
    updateURL(1, '', 'ALL');
  }, [updateURL]);

  // shouldReset 완료 처리
  const handleResetComplete = useCallback(() => {
    setShouldReset(false);
  }, []);

  // 새로고침 처리
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // 계산된 값들
  const contacts = contactListData?.data?.contactList || [];
  const totalCount = contactListData?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const hasError = !!error;
  const isEmpty = !isLoading && !hasError && contacts.length === 0;

  return {
    // 상태
    contacts,
    currentPage,
    totalPages,
    keyword,
    filter,
    isLoading,
    hasError,
    isEmpty,
    shouldReset,
    totalCount,

    // 액션
    handlePageChange,
    handleSearch,
    handleFilterChange,
    handleReset,
    handleResetComplete,
    handleRefresh,

    // 새로고침 함수 (모달에서 사용)
    refreshContactList: refetch,

    // 에러 정보
    error,
  };
};
